import React, { useCallback, useMemo, useState } from 'react';
import type { AddDiagnosisRequest, UpdateDiagnosisRequest } from '@features/patients';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined';
import { Box, Button, Chip, IconButton, useTheme } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridDensity,
  type GridRowId,
  type GridRowModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';

import type { Diagnosis } from '../types';

interface PatientDiagnosesEditCardProps {
  diagnoses: Diagnosis[];
  onAddDiagnosis: (request: AddDiagnosisRequest) => Promise<Diagnosis>;
  onUpdateDiagnosis: (diagnosisId: string, patch: UpdateDiagnosisRequest) => Promise<void>;
  onDeleteDiagnosis: (diagnosisId: string) => Promise<void>;
  isLoading?: boolean;
  patientId?: string;
  /** Source identifier for analytics */
  source?: string;
}

interface CustomToolbarProps {
  onAddRow: () => void;
  isLoading: boolean;
  buttonLabel: string;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({ onAddRow, isLoading, buttonLabel }) => {
  return (
    <GridToolbarContainer
      sx={{
        mb: 3,
        gap: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& .MuiButton-root': {
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 500,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </Box>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={onAddRow}
        disabled={isLoading}
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          fontWeight: 500,
          px: 3,
          bgcolor: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '&:disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled',
          },
        }}
      >
        {buttonLabel}
      </Button>
    </GridToolbarContainer>
  );
};

const formatDate = (date: string | null | undefined): string => {
  if (!date) return '';
  return date.split('T')[0];
};

const toIsoDate = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date) return value.toISOString().split('T')[0];
  return null;
};

const ALWAYS_VISIBLE_DIAGNOSIS_FIELDS = ['name', 'is_active', 'actions'];

export const PatientDiagnosesEditCard: React.FC<PatientDiagnosesEditCardProps> = ({
  diagnoses,
  onAddDiagnosis,
  onUpdateDiagnosis,
  onDeleteDiagnosis,
  isLoading = false,
  patientId,
  source = 'context_bar',
}) => {
  const theme = useTheme();
  const [density, setDensity] = useState<GridDensity>('compact');
  const apiRef = useGridApiRef();
  const { patient: patientTracking } = useEventTracking();

  const initialColumnVisibilityModel = useMemo(() => {
    const visibilityModel: GridColumnVisibilityModel = {};
    const fields = ['name', 'code', 'date_added', 'end_date', 'is_active', 'actions'];

    fields.forEach(field => {
      if (ALWAYS_VISIBLE_DIAGNOSIS_FIELDS.includes(field)) {
        visibilityModel[field] = true;
        return;
      }

      if (diagnoses.length === 0) {
        visibilityModel[field] = true;
        return;
      }

      const hasValue = diagnoses.some(diag => {
        const value = diag[field as keyof Diagnosis];
        return value !== null && value !== undefined && value !== '';
      });
      visibilityModel[field] = hasValue;
    });

    return visibilityModel;
  }, [diagnoses]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );

  const handleDeleteClick = useCallback(
    async (id: GridRowId) => {
      await onDeleteDiagnosis(id as string);
      if (patientId) {
        patientTracking.updated(patientId, {
          source,
          fields_updated: ['diagnoses_item_deleted'],
        });
      }
    },
    [onDeleteDiagnosis, patientId, patientTracking, source],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      const diagnosisId = newRow.diagnosis_id as string;
      const patch: UpdateDiagnosisRequest = {
        name: newRow.name ?? null,
        code: newRow.code ?? null,
        date_added: toIsoDate(newRow.date_added),
        end_date: toIsoDate(newRow.end_date),
        is_active: newRow.is_active ?? null,
      };

      try {
        await onUpdateDiagnosis(diagnosisId, patch);
        if (patientId) {
          patientTracking.updated(patientId, {
            source,
            fields_updated: ['diagnoses_item_edited'],
          });
        }
        return newRow;
      } catch {
        return oldRow;
      }
    },
    [onUpdateDiagnosis, patientId, patientTracking, source],
  );

  const handleAddRow = useCallback(async () => {
    const defaultName = `New Diagnosis ${diagnoses.length + 1}`;
    const created = await onAddDiagnosis({ name: defaultName, is_active: true });

    setTimeout(() => {
      if (apiRef.current && created.diagnosis_id) {
        apiRef.current.startCellEditMode({
          id: created.diagnosis_id,
          field: 'name',
        });
      }
    }, 100);

    if (patientId) {
      patientTracking.updated(patientId, {
        source: 'context_bar',
        fields_updated: ['diagnoses_item_added'],
      });
    }
  }, [diagnoses.length, onAddDiagnosis, apiRef, patientId, patientTracking]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Diagnosis',
        minWidth: 200,
        flex: 2,
        editable: true,
      },
      {
        field: 'code',
        headerName: 'Code',
        minWidth: 100,
        flex: 0.8,
        editable: true,
      },
      {
        field: 'date_added',
        headerName: 'Start Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.date_added;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.date_added),
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.end_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.end_date),
      },
      {
        field: 'is_active',
        headerName: 'Status',
        flex: 1,
        editable: true,
        type: 'boolean',
        renderCell: params => {
          const isActive = params.value !== false;
          return (
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                borderRadius: '999px',
                bgcolor: isActive ? 'rgba(103, 80, 164, 0.12)' : 'rgba(121, 116, 126, 0.12)',
                color: isActive ? 'rgba(103, 80, 164, 1)' : 'rgba(121, 116, 126, 1)',
                fontWeight: 500,
                border: 'none',
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        editable: false,
        renderCell: params => (
          <IconButton
            size="small"
            aria-label="Delete diagnosis"
            onClick={e => {
              e.stopPropagation();
              void handleDeleteClick(params.id);
            }}
            sx={{
              color: 'grey.500',
              '&:hover': {
                color: 'error.main',
              },
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleDeleteClick],
  );

  const rows = useMemo(() => {
    return diagnoses.map(diag => ({ ...diag, id: diag.diagnosis_id }));
  }, [diagnoses]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density={density}
          onDensityChange={newDensity => setDensity(newDensity)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          hideFooter
          showToolbar
          slots={{
            toolbar: () => (
              <CustomToolbar
                onAddRow={() => void handleAddRow()}
                isLoading={isLoading}
                buttonLabel="Add Diagnosis"
              />
            ),
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderRadius: '12px',
              fontWeight: 600,
              borderBottom: 'none',
            },
            '& .MuiDataGrid-row': {
              borderRadius: '8px',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};
